import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Field } from 'formik';
import { PickList } from 'primereact/picklist';
import { intlShape } from '../../../components/prop-types';

const messages = defineMessages({
  available: { id: 'attrs.available', defaultMessage: 'Available' },
  selected: { id: 'attrs.selected', defaultMessage: 'Selected' },
});

function surveyTemplate_itemTemplate(edge) {
  const {
    node: { id, title },
  } = edge;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <strong>{title}</strong>
      <span>{`#${id} `}</span>
    </div>
  );
}

function abilityy_itemTemplate(item) {
  const { displayName, competencyName } = item;
  return (
    <div className="p-grid">
      <div className="p-col-6">
        <strong>{displayName}</strong>
      </div>
      <div className="p-col-6">{competencyName}</div>
    </div>
  );
}

function flatten(arr) {
  return arr.reduce((flat, toFlatten) => flat.concat(Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten), []);
}

function abilitiesWithCompetencyInfo(competencies) {
  let abilities = competencies.map((competency) => {
    const a = competency.abilities.map(ability => ({
      ...ability,
      competencyName: competency.displayName,
      competencyId: competency.id,
    }));
    return a;
  });
  // flatten
  abilities = flatten(abilities);

  return abilities;
}

const templateAlphabetSorting = (first, second) => ((
  first.node.title.toUpperCase() > second.node.title.toUpperCase())
  ? 1
  : -1);

const abilityAlphabetSorting = (first, second) => ((
  first.displayName.toUpperCase() > second.displayName.toUpperCase())
  ? 1
  : -1);

function SurveyStep(props) {
  const {
    intl: { formatMessage },
    isCurrentStep,
    competencies,
    templates: { edges },
  } = props;

  const abilities = abilitiesWithCompetencyInfo(competencies);

  const sortedTemplates = edges.slice().sort(templateAlphabetSorting);
  const [templatesSource, setTemplatesSource] = useState(sortedTemplates);
  const [templatesTarget, setTemplatesTarget] = useState([]);
  const sortedTemplateTarget = templatesTarget.slice().sort(templateAlphabetSorting);

  const sortedAbilities = abilities.sort(abilityAlphabetSorting);
  const [abilitiesSource, setAbilitiesSource] = useState(sortedAbilities);
  const [abilitiesTarget, setAbilitiesTarget] = useState([]);
  const sortedAbilityTargets = abilitiesTarget.sort(abilityAlphabetSorting);

  if (!isCurrentStep) {
    return null;
  }

  return (
    <>
      <div className="p-col-12">
        <FormattedMessage
          id="accounts.add-client.survey-templates.heading"
          defaultMessage="Assign Survey Templates"
          tagName="h1"
        />
        <FormattedMessage
          id="accounts.add-client.survey-templates.description"
          defaultMessage="Allow scheduling surveys using the selected templates."
          tagName="p"
        />
      </div>
      <div className="p-col-12">
        <Field name="surveyTemplates">
          {({ field, form }) => {
            const onChange = (e) => {
              setTemplatesSource(e.source);
              setTemplatesTarget(e.target);
              // const ids = e.target.map(edge => edge.node.id);
              form.setFieldValue(field.name, e.target);
            };
            return (
              <PickList
                source={templatesSource}
                target={sortedTemplateTarget}
                itemTemplate={surveyTemplate_itemTemplate}
                onChange={onChange}
                sourceHeader={formatMessage(messages.available)}
                targetHeader={formatMessage(messages.selected)}
                showSourceControls={false}
                showTargetControls={false}
                responsive
              />
            );
          }}
        </Field>
      </div>
      <div className="p-col-12">
        <FormattedMessage
          id="accounts.add-client.survey-dimensions.heading"
          defaultMessage="Assign Survey Abilities"
          tagName="h1"
        />
        <FormattedMessage
          id="accounts.add-client.survey-dimensions.description"
          defaultMessage="Allow Survey Template creation using the selected abilities."
          tagName="p"
        />
        <div className="p-col-12">
          <Field name="surveyAbilities">
            {({ field, form }) => {
              const onChange = (e) => {
                setAbilitiesSource(e.source);
                setAbilitiesTarget(e.target);
                // const ids = e.target.map(item => item.id);
                form.setFieldValue(field.name, e.target);
              };
              return (
                <PickList
                  source={abilitiesSource}
                  target={sortedAbilityTargets}
                  itemTemplate={abilityy_itemTemplate}
                  onChange={onChange}
                  sourceHeader={formatMessage(messages.available)}
                  targetHeader={formatMessage(messages.selected)}
                  showSourceControls={false}
                  showTargetControls={false}
                  responsive
                />
              );
            }}
          </Field>
        </div>
      </div>
    </>
  );
}

SurveyStep.propTypes = {
  intl: intlShape.isRequired,
  isCurrentStep: PropTypes.bool.isRequired,
  competencies: PropTypes.arrayOf(PropTypes.object).isRequired,
  templates: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default injectIntl(SurveyStep);
